import React, { Component } from 'react';
import debounce from 'lodash.debounce';
import { ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { classNames, difference, getSlot, isContentfulString, isEmptyObject, isEqualShallow, isFunction, isUndefined, propagateData } from '@philips-internal/oswald-ui-utils';
import { FieldContext } from '../field/context/field-context.js';
import { LocatorInput } from './components/locator-input.js';
import { LocatorProperties } from './locator-types.js';
import './locator.css';
const LocatorContainer = {
    acceptedTypes: [
        ComponentType.FORM,
    ],
    name: 'fields',
};
class Locator extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            doSearch: false,
            isValidEntry: true,
            record: {},
            searchParameters: {},
        };
        this.updateValidations = (fromRecordChange) => {
            if (!this.context) {
                return;
            }
            const { data, loading = LocatorProperties.loading.defaultValue } = this.props;
            const { record, searchParameters, isValidEntry } = this.state;
            const hasSearchParametersAndNoData = !fromRecordChange && !isEmptyObject(searchParameters) && (!Array.isArray(data) || !data.length);
            const hasRecordAndNoSearchParameters = isEmptyObject(searchParameters) && !isEmptyObject(record);
            const isValidAndNotLoading = !loading && isValidEntry;
            const canSubmit = (isValidAndNotLoading || hasRecordAndNoSearchParameters) && !hasSearchParametersAndNoData;
            const validations = [{
                    message: 'tws.locator.result-not-found',
                    name: 'search-validator',
                    valid: canSubmit,
                }];
            this.context.updateChildValidations(validations);
        };
        this.$search = debounce(this.search, 300);
        this.onBlur = (event) => {
            const { onBlur } = this.props;
            if (isFunction(onBlur)) {
                onBlur(event);
            }
        };
        this.onFieldsChange = ({ entry, value }) => {
            if (!entry) {
                return;
            }
            const newSearchParameters = this.getNewSearchParameters(entry, value);
            this.setState({
                doSearch: true,
                searchParameters: newSearchParameters,
            }, this.$search);
        };
        this.onLocatorFieldSubmit = ({ record }) => {
            this.setState({ isValidEntry: true, record }, this.onRecordChange);
        };
    }
    componentDidMount() {
        const { value } = this.props;
        if (value) {
            this.updateRecordFromValue();
        }
        this.updateValidations();
    }
    componentDidUpdate(oldProps) {
        const { value: oldValue = {}, data: oldData } = oldProps;
        const { data, value = {} } = this.props;
        if (Array.isArray(data) && difference(data, Array.isArray(oldData) ? oldData : []).length > 0) {
            return this.updateRecordFromData();
        }
        if (value !== oldValue && !isEqualShallow(value, oldValue)) {
            return this.updateRecordFromValue();
        }
        if (oldProps.loading && !this.props.loading && !this.state.doSearch) {
            this.updateValidations();
        }
    }
    componentWillUnmount() {
        this.context?.resetChildValidations();
    }
    static formatPrettyValue(properties) {
        const { value, displayProp } = properties;
        return LocatorInput.formatPrettyValue({ displayProp, value });
    }
    getFinalPrettyValue() {
        const { displayProp = LocatorProperties.displayProp.defaultValue } = this.props;
        const { record } = this.state;
        return Locator.formatPrettyValue({ displayProp, value: record });
    }
    onRecordChange() {
        const { onChange, value: oldValue } = this.props;
        const { record } = this.state;
        this.updateValidations(true);
        if (isFunction(onChange)) {
            onChange(new ChangeEvent({
                oldValue,
                prettyValue: this.getFinalPrettyValue(),
                value: !isEmptyObject(record) ? record : undefined,
            }));
        }
    }
    updateRecordFromData() {
        const { searchParameters, record } = this.state;
        const { data = LocatorProperties.data.defaultValue } = this.props;
        if (isEmptyObject(searchParameters) && !isEmptyObject(record)) {
            return;
        }
        if (isEmptyObject(searchParameters) && isEmptyObject(record)) {
            return this.setState({ isValidEntry: true, record: {} }, this.onRecordChange);
        }
        const isValidEntry = Array.isArray(data) && data.length === 1;
        const changedData = data && isValidEntry ? data[0] : {};
        this.setState({ isValidEntry, record: changedData }, this.onRecordChange);
    }
    getNormalizedSearchParameters(record) {
        const { searchParameters } = this.state;
        if (isEmptyObject(record)) {
            return searchParameters;
        }
        return Object.keys(searchParameters)
            .filter(key => key in record && isContentfulString(record[key]))
            .reduce((obj, key) => ({ ...obj, [key]: record[key] }), {});
    }
    updateRecordFromValue() {
        const { record, searchParameters } = this.state;
        const { value } = this.props;
        if (isUndefined(value) || isEmptyObject(value)) {
            const isValidEntry = isEmptyObject(searchParameters);
            return this.setState({ isValidEntry, record: {} }, this.onRecordChange);
        }
        const normalizedRecord = { ...record, ...value };
        this.setState({ record: normalizedRecord, searchParameters: this.getNormalizedSearchParameters(normalizedRecord) }, this.onRecordChange);
    }
    search() {
        const { doSearch, searchParameters } = this.state;
        const { onNextPage } = this.props;
        if (!doSearch) {
            return;
        }
        const nextPage = () => {
            this.onRecordChange();
            if (isFunction(onNextPage)) {
                const shouldReset = isEmptyObject(searchParameters);
                onNextPage({ params: searchParameters, reset: shouldReset, skip: shouldReset });
            }
        };
        this.setState({ doSearch: false, isValidEntry: true, record: {} }, nextPage);
    }
    getNewSearchParameters(entry, value) {
        const { searchParameters } = this.state;
        const parameters = Object.entries({ ...searchParameters, [entry]: value });
        const accumulator = {};
        for (const [key, value] of parameters) {
            if (isContentfulString(value)) {
                accumulator[key] = value;
            }
        }
        return accumulator;
    }
    getFieldsValue() {
        const { readOnly } = this.props;
        const { searchParameters, record } = this.state;
        return readOnly ? record : { ...record, ...searchParameters };
    }
    getClassName(className) {
        const { loading = LocatorProperties.loading.defaultValue, strikethrough, valid, } = this.props;
        const { isValidEntry } = this.state;
        const notValid = !isValidEntry || !valid;
        return classNames({
            [`${className}`]: Boolean(className),
            'c-locator__invalid-field': !loading && notValid,
            'u-text-strikethrough': Boolean(strikethrough),
        });
    }
    renderFields() {
        const { children, onFocus, readOnly, sensitiveData = LocatorProperties.sensitiveData.defaultValue, } = this.props;
        const fieldsAreaChildren = getSlot(children, 'fields');
        const fieldProps = {
            className: this.getClassName('c-locator__field'),
            editing: !readOnly,
            onBlur: this.onBlur,
            onChange: this.onFieldsChange,
            onFocus,
            readOnly,
            sensitiveData,
        };
        return propagateData(fieldsAreaChildren, this.getFieldsValue(), fieldProps);
    }
    renderLocatorField() {
        const { contextData, displayProp = LocatorProperties.displayProp.defaultValue, enabled = LocatorProperties.enabled.defaultValue, fragment, inputEnabled = LocatorProperties.inputEnabled.defaultValue, loading = LocatorProperties.loading.defaultValue, modalType = LocatorProperties.modalType.defaultValue, onFocus, placeholder = LocatorProperties.placeholder.defaultValue, readOnly, sensitiveData = LocatorProperties.sensitiveData.defaultValue, } = this.props;
        const locatorInputProps = {
            className: this.getClassName(),
            contextData,
            displayProp,
            enabled,
            fragment,
            inputEnabled,
            loading,
            modalType,
            onBlur: this.onBlur,
            onChange: this.onFieldsChange,
            onFocus,
            onSubmit: this.onLocatorFieldSubmit,
            placeholder,
            readOnly,
            sensitiveData,
            value: this.getFieldsValue(),
        };
        return React.createElement(LocatorInput, { ...locatorInputProps });
    }
    render() {
        const { className, uuid } = this.props;
        const wrapperClassName = classNames({
            [`${className}`]: Boolean(className),
        });
        return (React.createElement("div", { className: wrapperClassName },
            React.createElement(Container, { acceptedTypes: LocatorContainer.acceptedTypes, className: "c-locator", slot: LocatorContainer.name, uuid: uuid },
                this.renderFields(),
                this.renderLocatorField())));
    }
}
Locator.contextType = FieldContext;
export { Locator, LocatorContainer, };
//# sourceMappingURL=locator.js.map